import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle'
  | 'subtitleLight'
  | 'paragraph'
  | 'bodyBold'
  | 'body'
  | 'bodyLight'
  | 'caption'
  | 'breadcrumbs'
  | 'footer'
  | 'filteredLink'
  | 'link'
  | 'metadata'
  | 'no-style';

type TypographyOwnProps<E extends React.ElementType = React.ElementType> = {
  variant?: Variant;
  as?: E;
  children?: ReactNode;
} & HTMLAttributes<HTMLElement>;

export type TypographyProps<
  E extends React.ElementType
> = TypographyOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof TypographyOwnProps>;

const VARIANT_MAPS: Record<Variant, string> = {
  h1: `text-lg md:text-5xl font-black leading-9`,
  h2: `text-lg leading-8 md:text-3xl font-bold`,
  h3: `text-lg md:text-2xl font-bold leading-7`,
  h4: `text-lg md:text-xl font-bold leading-6`,
  h5: `text-lg font-semibold leading-5`,
  h6: `text-md font-semibold leading-5 tracking-wide`,
  subtitle: `text-base font-normal leading-6`,
  subtitleLight: `text-sm md:text-base font-light leading-4.5`,
  bodyBold: `text-md font-bold leading-3.5`,
  body: `text-md font-normal leading-3.5`,
  bodyLight: `text-md md:text-sm font-light leading-2.5`,
  paragraph: `text-lg font-normal leading-5`,
  caption: `text-md font-normal italic leading-5`,
  breadcrumbs: `text-md font-normal leading-5`,
  footer: `text-xs font-light leading-1 tracking-wide`,
  filteredLink: `text-lg font-medium leading-5`,
  link: `text-lg font-medium leading-5`,
  metadata: `text-md font-medium leading-5 tracking-wide`,
  'no-style': ``,
};

const defaultElement = `p`;

/**
 * @component Typography
 */
const Typography: <E extends React.ElementType = typeof defaultElement>(
  props: TypographyProps<E>,
) => React.ReactElement | null = forwardRef(
  (props: TypographyOwnProps, ref: React.Ref<Element>) => {
    const {
      className,
      variant = `paragraph`,
      as = defaultElement,
      children,
      ...rest
    } = props;

    const Component = as;

    const classes = clsx(`font-sans`, VARIANT_MAPS[variant], className);
    return (
      <Component ref={ref} className={classes} {...rest}>
        {children}
      </Component>
    );
  },
);

export default Typography;
